import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import datascienceandai from '../images/datascienceandai.png';
import './TrainingVideos.css';

const TrainingVideos = ({ departmentName }) => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [enrolledVideos, setEnrolledVideos] = useState(new Set());
  const [enrolling, setEnrolling] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      if (!departmentName) return;

      try {
        setLoading(true);
        setError(null);

        const userEmail = localStorage.getItem('userEmail');
        const promises = [
          apiFetch(
            `${window._env_.API_BASE_URL}/training-videos/?department_name=${encodeURIComponent(departmentName)}&active_only=true`
          )
        ];

        if (userEmail) {
          promises.push(
            apiFetch(
              `${window._env_.API_BASE_URL}/training-videos/my-videos?user_email=${encodeURIComponent(userEmail)}`
            )
          );
        }

        const [videosData, enrolledData] = await Promise.all(promises);

        setVideos(videosData || []);

        if (enrolledData) {
          const enrolledIds = new Set(enrolledData.map(v => v.video_id));
          setEnrolledVideos(enrolledIds);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
        setVideos([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [departmentName]);

  const handleEnroll = async (video) => {
    const userEmail = localStorage.getItem('userEmail');
    const token = localStorage.getItem('token');

    if (!userEmail || !token) {
      alert('Please log in to enroll in training videos');
      return;
    }

    setEnrolling(prev => ({ ...prev, [video.video_id]: true }));

    try {
      const url = `${window._env_.API_BASE_URL}/training-videos/${video.video_id}/enroll`;

      await apiFetch(url, {
        method: 'POST',
        body: JSON.stringify({})
      });

      setEnrolledVideos(prev => new Set([...prev, video.video_id]));

      const snackbar = document.createElement('div');
      snackbar.textContent = 'Successfully enrolled! Redirecting...';
      snackbar.style.cssText =
        'position: fixed; top: 20px; left: 50%; transform: translateX(-50%); background-color: #4CAF50; color: white; padding: 12px 24px; border-radius: 4px; z-index: 1000;';
      document.body.appendChild(snackbar);

      setTimeout(() => {
        snackbar.remove();
        const deptName = encodeURIComponent(video.department_name || departmentName);
        navigate(`/${deptName}/${video.video_id}/training-videos`);
      }, 1500);
    } catch (err) {
      if (err.message?.includes('already enrolled')) {
        const deptName = encodeURIComponent(video.department_name || departmentName);
        navigate(`/${deptName}/${video.video_id}/training-videos`);
      } else {
        alert(`Enrollment failed: ${err.message}`);
      }
    } finally {
      setEnrolling(prev => ({ ...prev, [video.video_id]: false }));
    }
  };

  const handleWatch = (video) => {
    const deptName = encodeURIComponent(video.department_name || departmentName);
    navigate(`/${deptName}/${video.video_id}/training-videos`);
  };

  if (loading) {
    return (
      <div className="training-videos-section">
        <h2>CriticalRiver Training Videos</h2>
        <div className="tv-loading">Loading videos...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="training-videos-section">
        <h2>CriticalRiver Training Videos</h2>
        <div className="tv-error">Error loading videos: {error}</div>
      </div>
    );
  }

  if (videos.length === 0) return null;

  return (
    <div className="training-videos-section">
      <h2>CriticalRiver Training Videos</h2>

      <div className="tv-videos-grid">
        {videos.map((video) => (
          <div key={video.video_id} className="tv-video-card">
            <button
              type="button"
              className="tv-thumbnail-button"
              onClick={() => setSelectedVideo(video)}
              aria-label={`Preview ${video.title}`}
            >
              <img
                src={video.thumbnail_url?.trim() || datascienceandai}
                alt={video.title}
                className="tv-video-thumbnail"
                onError={(e) => {
                  if (e.target.src !== datascienceandai) {
                    e.target.src = datascienceandai;
                  }
                }}
              />
            </button>

            <div className="tv-video-info">
              <h3>{video.title}</h3>
              {video.description && <p>{video.description}</p>}
              {video.duration && <span className="tv-duration">⏱ {video.duration}</span>}
            </div>

            <div className="tv-video-actions">
              {enrolledVideos.has(video.video_id) ? (
                <button
                  className="tv-watch-btn"
                  onClick={() => handleWatch(video)}
                >
                  Watch Video
                </button>
              ) : (
                <button
                  className="tv-enroll-btn"
                  onClick={() => handleEnroll(video)}
                  disabled={enrolling[video.video_id]}
                >
                  {enrolling[video.video_id] ? 'Enrolling...' : 'Enroll Now'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {selectedVideo && (
        <div className="tv-modal">
          <div className="tv-modal-content">
            <button
              className="tv-close-btn"
              onClick={() => setSelectedVideo(null)}
              aria-label="Close preview"
            >
              ×
            </button>

            <h2>{selectedVideo.title}</h2>

            {selectedVideo.video_url && (
              <video
                controls
                autoPlay
                className="tv-video-player"
                src={selectedVideo.video_url}
              >
                <track kind="captions" src="" label="English" />
                Your browser does not support the video tag.
              </video>
            )}

            {selectedVideo.description && (
              <p className="tv-video-description">{selectedVideo.description}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

TrainingVideos.propTypes = {
  departmentName: PropTypes.string.isRequired
};

export default TrainingVideos;
